import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import { exchangeDate, getPractice } from "../../../../helpers";
import { fetchSingleCarePlanForm } from "../../../../services/forms";

export default function CarePlanPrint() {
  const [formData, setFormData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const formId = queryParams.get('form_id');
  const formSize = queryParams.get('size');
  const accountId = getPractice("account_id");
  const locationId = getPractice("location_id");

  useEffect(() => {
    if(formId !== null){
      getReferralData( formId );
    } else {
      navigate(-1);
    }
  }, [formId]);

  const getReferralData = async ( formId ) => {
    const response = await fetchSingleCarePlanForm( accountId, locationId, formId );
    if (response) {
      if(response.data === null) {
        window.close();
      }
      setFormData(response?.data);
      setIsDataLoaded(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (isDataLoaded) {
      handlePrint();
    }
  }, [isDataLoaded]);

  const handleCloseWindow = () => {
    window.close();
  };

  useEffect(() => {
    const pageSize = formSize == "A4" ? "A4" : "Letter";
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      @media print {
        @page {
          size: ${pageSize};
        }
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [formSize]);

  useDocumentTitle("Patient Care Plan Form");

  return (
    <div ref={contentRef} className="docpro_signout_wrap">
      <div className="docpro_signout hipaa_table">
        <table className="dashed" style={{ width: '60%', marginLeft: 'auto' }}>
          <tbody>
            <tr>
              <td style={{ borderTop: 'none', width: '120px' }}>MRN</td>
              <td style={{ borderTop: 'none', width: '20px' }}>:</td>
              <td style={{ borderTop: 'none' }}>{formData?.mrn}</td>
            </tr>
            <tr>
              <td>Patient Name</td>
              <td>:</td>
              <td>{formData?.patient_name}</td>
            </tr>
            <tr>
              <td>DOB</td>
              <td>:</td>
              <td>{exchangeDate(formData?.dob)}</td>
            </tr>
            <tr>
              <td>DOS</td>
              <td>:</td>
              <td>{exchangeDate(formData?.dos)}</td>
            </tr>
          </tbody>
        </table>

        <div className="text-center mb-2">
          <h5 className="mb-1">Plan for your Health</h5>
          <p style={{ marginTop: '0' }}> Plan for (selected appropriate plan/s:)</p>
          <input type="text" className="form-control" style={{ minHeight: '40px' }} readOnly defaultValue={formData?.pyh} />
        </div>

        <center>
          <h5 className="mb-1">We have agreed that you will start/continue to:</h5>
          <p style={{ margin: '0 0 8px' }}>(List goals, tests, new or continuing treatments/meds and referrals)</p>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '8rem' }}>{formData?.treatments_box1}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <center>
          <h5 className="mb-1">Self-Management Goals <br />These are goals you have set for yourself to improve your health:</h5>
          <p style={{ margin: '0 0 8px' }}>(List all relevant self-management goals)</p>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '8rem' }}>{formData?.relevant_box1}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <center>
          <h5 style={{ margin: '0 0 8px' }}>Patient preferences and functional lifestyle goals</h5>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '7rem' }}>{formData?.p_preferences}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <center>
          <h5 style={{ margin: '0 0 8px' }}>Potential barriers to achieving listed goals or treatments:</h5>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '8rem' }}>{formData?.treatments}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {formSize == 'A4' ?
          <div className="spacing_6"></div>
        :
          <div className="spacing_7 mt-1"></div>
        }

        <center>
          <h5 style={{ margin: '0 0 8px' }}>Potential solutions for overcoming barriers:</h5>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '8rem' }}>{formData?.solution}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex items-center mb-2">
          <p className="mr-2 mb-0 fw-bold">Health Literacy assessed:</p>
          <input className="form-check-input mr-2" type="checkbox" checked={formData?.he_li_asse == 1} disabled />
          <span className="mr-2">Yes</span>
          <span className="mr-2">Or</span>
          <input className="form-check-input mr-2 pl-2" type="checkbox" checked={formData?.he_li_asse == 0} disabled />
          <span>No</span>
        </div>
        <div className="flex items-center mb-2">
          <p className="mr-2 mb-0 fw-bold">Assessed Understanding of Medications:</p>
          <input className="form-check-input mr-2" type="checkbox" checked={formData?.asse_un_medi == 1} disabled />
          <span className="mr-2">Yes</span>
          <span className="mr-2">Or</span>
          <input className="form-check-input mr-2 pl-2" type="checkbox" checked={formData?.asse_un_medi == 0} disabled />
          <span>No</span>
        </div>
        <div className="flex items-center mb-2">
          <p className="mr-2 mb-0 fw-bold">Assessed Response to Medication and Barriers to Adherence:</p>
          <input className="form-check-input mr-2" type="checkbox" checked={formData?.asse_medi_bar_adh == 1} disabled />
          <span className="mr-2">Yes</span>
          <span className="mr-2">Or</span>
          <input className="form-check-input mr-2 pl-2" type="checkbox" checked={formData?.asse_medi_bar_adh == 0} disabled />
          <span>No</span>
        </div>

        <center>
          <h5 style={{ margin: '0 0 8px' }}>Medication related issues:</h5>
        </center>

        <div className="docpro_table_1 mb-2" style={{ boxShadow: 'none', borderRadius: 0 }}>
          <table className="docpro_mb_0 docpro_primary_color" style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ border: 0, padding: 0 }}>
                  <p style={{ margin: '6px', minHeight: '8rem' }}>{formData?.issues}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex mb-2" style={{ alignItems: 'start' }}>
          <input className="form-check-input" style={{ minWidth: '13px', marginTop: '0.25em' }} type="checkbox" checked={formData?.p_agree == 1} disabled />
          <label className="form-check-label">
            Patient agrees with the goals and treatments outlined in this care plan. Patient is given original copy of care plan once scanned into the EMR.
          </label>
        </div>

        <div className="flex">
          <div style={{ width: '50%' }}>
            <div className="flex mb-2">
              <p className="mb-0 pe-3 fw-bold">Patient Signature</p><span className="mx-1">:</span>
              <div className="rounded p-1" style={{ height: "80px", width: "160px" }}>
                {formData?.p_signature !== null &&
                  <img src={`${formData?.p_signature}`} className="img-fluid d-block img_print" alt="signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                }
              </div>
            </div>
            <div className="pb-1 flex">
              <p className="mb-0 pe-3 fw-bold">Date</p><span className="mx-1">:</span> {exchangeDate(formData?.p_signature_date)}
            </div>
          </div>
          <div style={{ width: '50%' }}>
            <div className="flex mb-2">
              <p className="mb-0 pe-3 fw-bold">Provider Signature</p><span className="mx-1">:</span>
              <div className="rounded p-1" style={{ height: "80px", width: "160px" }}>
                {formData?.pro_signature !== null &&
                  <img src={`${formData?.pro_signature}`} className="img-fluid d-block img_print" alt="signature" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                }
              </div>
            </div>
            <div className="pb-1 flex">
              <p className="mb-0 pe-3 fw-bold">Date</p><span className="mx-1">:</span> {exchangeDate(formData?.pro_signature_date)}
            </div>
          </div>
        </div>
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={handleCloseWindow}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        <button
          className="docpro_invoice_btn docpro_color1"
          onClick={handlePrint}
        >
          <span className="docpro_btn_icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ionicon"
              viewBox="0 0 512 512"
            >
              <path
                d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H100a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <rect
                x={128}
                y={240}
                width={256}
                height={208}
                rx="24.32"
                ry="24.32"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <path
                d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <circle cx={392} cy={184} r={24} fill="currentColor" />
            </svg>
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
      </div>
    </div>
  );
}
